import React from "react";
import { SEGMENTS, ASSETS } from "@/data/content";
import { cn } from "@/lib/utils";

/**
 * Renders the original HR Anexi X-model artwork exactly as supplied (no recolouring, no
 * redesign) and lays invisible interactive hotspots on top of it. Hovering or clicking a
 * segment drives the detail panel beside the diagram; the artwork itself stays untouched so
 * nothing is drawn over the printed shapes. A keyboard focus ring is the only visible overlay.
 *
 * Hotspot polygons are calibrated against the source image's natural pixel size (1080x1080)
 * so they line up with the five printed segments regardless of how the image is scaled.
 */
const VIEW_W = 1080;
const VIEW_H = 1080;

const HOTSPOTS = {
  engaged: "556,287 721,449 391,449",
  "almost-engaged": "391,449 721,449 792,519 320,519",
  "honeymooners-hamsters": "320,519 556,519 556,669 332,669 249,588",
  "crash-burners": "556,519 792,519 863,588 780,669 556,669",
  disengaged: "332,669 780,669 556,889",
};

export const XModelImage = ({
  selected = null,
  hovered = null,
  onSelect,
  onHover,
  interactive = true,
  className,
  testId = "x-model-image",
  ariaLabel = "HR Anexi engagement X-model: satisfaction and contribution crossing to reveal five employee segments",
}) => {
  const activeId = hovered || selected;

  return (
    <div className={cn("relative", className)} data-testid={testId}>
      <img
        src={ASSETS.xModelPhoto}
        alt={ariaLabel}
        className={cn(
          "block h-auto w-full select-none transition-transform duration-500 [transition-timing-function:var(--ease-premium)]",
          interactive && activeId && "scale-[1.012]"
        )}
        draggable={false}
      />
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="absolute inset-0 h-full w-full"
        role={interactive ? "group" : "img"}
        aria-hidden={interactive ? undefined : true}
        data-testid={`${testId}-hotspots`}
      >
        {SEGMENTS.map((s) => {
          const isActive = activeId === s.id;
          const isSelected = selected === s.id;
          const handlers = interactive
            ? {
                role: "button",
                tabIndex: 0,
                "aria-pressed": isSelected,
                "aria-label": `${s.name}: ${s.levelLine}`,
                onClick: () => onSelect && onSelect(s.id),
                onMouseEnter: () => onHover && onHover(s.id),
                onMouseLeave: () => onHover && onHover(null),
                onFocus: () => onHover && onHover(s.id),
                onBlur: () => onHover && onHover(null),
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect && onSelect(s.id);
                  }
                },
              }
            : {};
          return (
            <polygon
              key={s.id}
              points={HOTSPOTS[s.id]}
              fill="transparent"
              stroke="transparent"
              strokeWidth={4}
              strokeLinejoin="round"
              pointerEvents="all"
              className={cn("x-hotspot", interactive && "cursor-pointer outline-none")}
              data-testid={`${testId}-segment-${s.id}`}
              data-active={isActive || undefined}
              {...handlers}
            />
          );
        })}
      </svg>
    </div>
  );
};
